import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { AthleteSummary } from './types';
import { Avatar } from './Avatar';
import { loadRecentAthletes, pushRecentAthlete } from './utils/storage';

interface Props {
  athletes: AthleteSummary[];
  onPick: (athleteId: number) => void;
  onClose: () => void;
}

export function AthletePicker({ athletes, onPick, onClose }: Props) {
  const [query, setQuery] = useState('');
  const [recentIds, setRecentIds] = useState<number[]>(loadRecentAthletes());
  const inputRef = useRef<HTMLInputElement>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    inputRef.current?.focus();
    const raf = requestAnimationFrame(() => setMounted(true));
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const modalTransition: React.CSSProperties = {
    transition:
      'transform var(--motion-picker-duration) var(--motion-picker-easing), ' +
      'opacity var(--motion-picker-duration) var(--motion-picker-easing)',
    transform: mounted ? 'translateY(0)' : 'translateY(8px)',
    opacity: mounted ? 1 : 0,
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return athletes;
    return athletes.filter((a) => a.full_name.toLowerCase().includes(q));
  }, [athletes, query]);

  const recents = recentIds
    .map((id) => athletes.find((a) => a.id === id))
    .filter((a): a is AthleteSummary => !!a);

  const handlePick = (id: number) => {
    pushRecentAthlete(id);
    setRecentIds(loadRecentAthletes());
    onPick(id);
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="mt-24 w-[480px] cgm-bg-primary border border-[var(--card-border)]"
        style={modalTransition}
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          type="text"
          placeholder="Search athletes…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="cgm-input w-full px-3 py-2"
        />
        {!query && recents.length > 0 && (
          <Section title="Recent" athletes={recents} onPick={handlePick} />
        )}
        <Section
          title={query ? 'Results' : 'All'}
          athletes={filtered}
          onPick={handlePick}
        />
      </div>
    </div>
  );
}

function Section({
  title, athletes, onPick,
}: { title: string; athletes: AthleteSummary[]; onPick: (id: number) => void }) {
  if (athletes.length === 0) return null;
  return (
    <div className="border-t border-[var(--card-border)]">
      <div className="px-3 py-1 text-xs cgm-text-muted">{title}</div>
      <ul>
        {athletes.map((a) => {
          const meta = [
            a.position,
            a.sport,
            a.jersey_number ? `#${a.jersey_number}` : null,
          ].filter(Boolean).join(' · ');
          return (
            <li key={a.id}>
              <button
                type="button"
                className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-neutral-100 dark:hover:bg-neutral-800"
                onClick={() => onPick(a.id)}
              >
                <Avatar fullName={a.full_name} size={32} />
                <div className="flex flex-col min-w-0">
                  <span className="cgm-text-primary truncate">{a.full_name}</span>
                  {meta && (
                    <span className="text-xs cgm-text-muted truncate">{meta}</span>
                  )}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
